import { useCallback, useMemo, useState } from "react";
import { showSuccessToast } from "../../../components/toast/toastBus";
import { uploadFile } from "../../../services/fileService";

import { fetchArmasAll, createArma, updateArma, deleteArma } from "../api/armasApi";
import { createReparacion, updateReparacion, deleteReparacion } from "../api/reparacionesApi";
import { fetchTipoArmas } from "../api/enumsApi";
import { fetchPersonasAll, fetchTitulares } from "../api/personasApi";

import { filterAndSortArmas } from "../domain/armasSearch";

export function useArmas() {
    /* ----- DATA STATE ----- */
    const [armas, setArmas] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [tiposArma, setTiposArma] = useState([]);
    const [titulares, setTitulares] = useState([]);

    /* ----- MODALES / INFO ----- */
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState(null); // {type:'arma'|'reparacion', id}
    const [infoModal, setInfoModal] = useState({ open: false, title: "", message: "" });

    /* ----- UI STATE (Acordeones) ----- */
    const [openArmas, setOpenArmas] = useState(new Set());
    const [openRepByArma, setOpenRepByArma] = useState({});

    const toggleArma = (id) =>
        setOpenArmas((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    const isArmaOpen = (id) => openArmas.has(id);

    const toggleRep = (armaId, repKey) =>
        setOpenRepByArma((prev) => {
            const set = new Set(prev[armaId] || []);
            set.has(repKey) ? set.delete(repKey) : set.add(repKey);
            return { ...prev, [armaId]: set };
        });
    const isRepOpen = (armaId, repKey) => openRepByArma[armaId]?.has(repKey);

    /* ----- FORM ARMA ----- */
    const [formVisible, setFormVisible] = useState(false);
    const [editingArma, setEditingArma] = useState(null);
    const [fileGuiaArma, setFileGuiaArma] = useState(null);

    const emptyArmaForm = {
        modelo: "",
        marca: "",
        pais: "",
        numeroGuia: "",
        numeroSerie: "",
        calibre: "",
        tipoArma: "",
        proximaReparacion: "",
        personaId: "",
        titularId: "",
        rutaDocumentoGuiaArma: "",
        enlaces: [],
    };

    const [formData, setFormData] = useState({ ...emptyArmaForm });

    /* ----- FORM REPARACIÓN ----- */
    const [repairVisible, setRepairVisible] = useState(false);
    const [selectedRepairArma, setSelectedRepairArma] = useState(null);
    const [editingRepair, setEditingRepair] = useState(null);

    const [repairFormData, setRepairFormData] = useState({
        descripcion: "",
        fechaInicio: "",
        fechaFin: "",
        precio: "",
    });

    /* ----- BUSCADOR ----- */
    const [q, setQ] = useState("");

    /* ----- FETCH ALL ----- */
    const fetchAll = useCallback(async () => {
        try {
            const [arm, per, tip, tit] = await Promise.all([
                fetchArmasAll(),
                fetchPersonasAll(),
                fetchTipoArmas(),
                fetchTitulares().catch(() => []),
            ]);
            setArmas(Array.isArray(arm) ? arm : []);
            setPersonas(Array.isArray(per) ? per : []);
            setTiposArma(Array.isArray(tip) ? tip : []);
            setTitulares(Array.isArray(tit) ? tit : []);
        } catch (e) {
            alert("Error cargando datos de armas.");
        }
    }, []);

    /* ----- MAPAS RÁPIDOS ----- */
    const personaById = useMemo(
        () => Object.fromEntries((personas || []).map((p) => [p.id, p])),
        [personas]
    );

    const titularById = useMemo(
        () => Object.fromEntries((titulares || []).map((t) => [t.id, t])),
        [titulares]
    );

    const list = useMemo(() => {
        return filterAndSortArmas({ armas, q, personaById, titularById });
    }, [armas, q, personaById, titularById]);

    /* ----- CRUD ARMA ----- */
    const openCreate = () => {
        setEditingArma(null);
        setFormData({ ...emptyArmaForm });
        setFileGuiaArma(null);
        setFormVisible(true);
    };

    const handleEditArma = (arma) => {
        setEditingArma(arma);
        setFormData({
            modelo: arma.modelo || "",
            marca: arma.marca || "",
            pais: arma.pais || "",
            numeroGuia: arma.numeroGuia || "",
            numeroSerie: arma.numeroSerie || "",
            calibre: arma.calibre || "",
            tipoArma: arma.tipoArma || "",
            proximaReparacion: arma.proximaReparacion || "",
            rutaDocumentoGuiaArma: arma.rutaDocumentoGuiaArma || "",
            enlaces: arma.enlaces || [],
            titularId: arma.titularId !== undefined && arma.titularId !== null ? String(arma.titularId) : "",
            personaId: arma.personaId !== undefined && arma.personaId !== null ? String(arma.personaId) : "",
        });
        setFileGuiaArma(null);
        setFormVisible(true);
    };

    const handleArmaChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const submitArma = async (e) => {
        e.preventDefault();
        try {
            let uploaded = null;
            if (fileGuiaArma) uploaded = await uploadFile(fileGuiaArma, "armas/guias");

            const payload = {
                ...formData,
                titularId: formData.titularId === "" ? null : Number(formData.titularId),
                personaId: formData.personaId === "" ? null : Number(formData.personaId),
                rutaDocumentoGuiaArma: uploaded?.url ?? formData.rutaDocumentoGuiaArma ?? null,
                enlaces: (formData.enlaces || []).map((s) => s.trim()).filter(Boolean),
            };

            const esEdicion = !!editingArma;
            if (esEdicion) await updateArma(editingArma.id, payload);
            else await createArma(payload);

            setFormVisible(false);
            setEditingArma(null);
            setFileGuiaArma(null);
            await fetchAll();
            showSuccessToast(esEdicion ? "Arma actualizada con éxito" : "Arma creada con éxito");
        } catch {
            alert("Error guardando el arma.");
        }
    };

    /* ----- REPARACIONES ----- */
    const openRepair = (arma, rep = null) => {
        setSelectedRepairArma(arma);
        setEditingRepair(rep);

        setRepairFormData(
            rep
                ? {
                    descripcion: rep.descripcion || "",
                    fechaInicio: rep.fechaInicio || "",
                    fechaFin: rep.fechaFin || "",
                    precio: rep.precio ?? "",
                }
                : { descripcion: "", fechaInicio: "", fechaFin: "", precio: "" }
        );

        setRepairVisible(true);
    };

    const closeRepair = () => {
        setRepairVisible(false);
        setEditingRepair(null);
        setSelectedRepairArma(null);
    };

    const handleRepairChange = (e) => {
        const { name, value } = e.target;
        setRepairFormData((p) => ({ ...p, [name]: value }));
    };

    const submitRepair = async (e) => {
        e.preventDefault();
        if (!selectedRepairArma) return;

        try {
            const payload = {
                ...repairFormData,
                armaId: selectedRepairArma.id,
                precio: parseFloat(repairFormData.precio || "0"),
            };

            if (editingRepair) await updateReparacion(editingRepair.id, payload);
            else await createReparacion(payload);

            closeRepair();
            await fetchAll();
            showSuccessToast("Reparación guardada con éxito");
        } catch {
            alert("Error guardando la reparación.");
        }
    };

    /* ----- DELETE FLOW ----- */
    const requestDeleteArma = (id) => {
        setConfirmTarget({ type: "arma", id });
        setShowConfirm(true);
    };

    const requestDeleteRepair = (id) => {
        setConfirmTarget({ type: "reparacion", id });
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        if (!confirmTarget) return;

        try {
            if (confirmTarget.type === "arma") {
                const res = await deleteArma(confirmTarget.id);
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    setInfoModal({
                        open: true,
                        title: "No se puede eliminar",
                        message: text || "No se puede eliminar el arma porque puede estar referenciada.",
                    });
                    return;
                }
            } else {
                const res = await deleteReparacion(confirmTarget.id);
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    setInfoModal({
                        open: true,
                        title: "No se puede eliminar",
                        message: text || "No se pudo eliminar la reparación.",
                    });
                    return;
                }
            }

            await fetchAll();
        } catch {
            setInfoModal({ open: true, title: "No se puede eliminar", message: "Error al eliminar." });
        } finally {
            setShowConfirm(false);
            setConfirmTarget(null);
        }
    };

    return {
        // data
        armas,
        personas,
        tiposArma,
        titulares,
        personaById,
        titularById,
        list,

        // search
        q,
        setQ,

        // accordions
        toggleArma,
        isArmaOpen,
        toggleRep,
        isRepOpen,

        // arma form
        formVisible,
        setFormVisible,
        editingArma,
        formData,
        setFormData,
        fileGuiaArma,
        setFileGuiaArma,
        openCreate,
        handleEditArma,
        handleArmaChange,
        submitArma,

        // repair form
        repairVisible,
        selectedRepairArma,
        editingRepair,
        repairFormData,
        openRepair,
        closeRepair,
        handleRepairChange,
        submitRepair,

        // modals
        showConfirm,
        setShowConfirm,
        confirmTarget,
        setConfirmTarget,
        confirmDelete,
        requestDeleteArma,
        requestDeleteRepair,
        infoModal,
        setInfoModal,

        // fetch
        fetchAll,
    };
}
